import React, { useState } from "react";
import { Modal } from "antd";
import Typography from "style-guide/component/Typography";
import Button from "style-guide/component/Button";
import Input from "style-guide/component/Input";

import {
  useStripe,
  useElements,
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement,
} from "@stripe/react-stripe-js";
import { StripeCardNumberElementOptions } from "@stripe/stripe-js";
import { addPaymentMethod } from "../../../redux/slice/rfpModuleSlice/subscription";
import { useDispatch } from "react-redux";
import { AppDispatch } from "redux/store";

interface AddPaymentMethodModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: () => void;
  isLoading?: boolean;
  setIsAddingPayment: (isAddingPayment: boolean) => void;
}

const ELEMENT_OPTIONS : StripeCardNumberElementOptions = {
  showIcon: true,
  style: {
    base: {
      fontSize: "16px",
      color: "#1f2937",
      fontFamily: '"Helvetica Neue", Helvetica, sans-serif',
      fontSmoothing: "antialiased",
      "::placeholder": {
        color: "#9ca3af",
      },
      iconColor: "#6b7280",
    },
    invalid: {
      color: "#ef4444",
      iconColor: "#ef4444",
    },
    complete: {
      color: "#059669",
      iconColor: "#059669",
    },
  },
  disableLink: true,
};

const AddPaymentMethodModal: React.FC<AddPaymentMethodModalProps> = ({
  isOpen,
  onClose,
  onSave,
  isLoading = false,
  setIsAddingPayment,
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const dispatch = useDispatch<AppDispatch>();

  const [cardholderName, setCardholderName] = useState("");
  const [error, setError] = useState<string | null>(null);

  const handleSave = async () => {
    if (!stripe || !elements) {
      setError("Stripe.js has not loaded yet.");
      return;
    }

    if (!cardholderName.trim()) {
      setError("Cardholder name is required.");
      return;
    }

    const cardNumberElement = elements.getElement(CardNumberElement);
    if (!cardNumberElement) {
      setError("Card number element not found.");
      return;
    }
    setIsAddingPayment(true);

    const { token, error: stripeError } = await stripe.createToken(
      cardNumberElement,
      { name: cardholderName }
    );

    if (stripeError) {
      setError(stripeError.message || "Failed to generate token.");
      return;
    }

    if (token) {
      const status = await dispatch(addPaymentMethod({ token: token.id }));
      if (status === 200) {
        onSave();
      } else {
        setIsAddingPayment(false);
      }
    }
  };

  const handleCancel = () => {
    setCardholderName("");
    setError(null);
    onClose();
  };

  return (
    <Modal
      title={"Add Payment Method"}
      open={isOpen}
      onCancel={handleCancel}
      footer={null}
      width={500}
      centered
      maskClosable={!isLoading}
      closable={!isLoading}
      destroyOnClose
    >
      <div className="p-1 space-y-4">
        {/* Cardholder Name */}
        <div>
          <Typography variant="Base" className="mb-2 text-gray-900">
            Cardholder Name
          </Typography>
          <Input
            type="text"
            value={cardholderName}
            onChange={(e) => setCardholderName(e.target.value)}
            placeholder="Enter cardholder name"
            className="w-full"
            disabled={isLoading}
          />
        </div>

        {/* Card Number */}
        <div>
          <Typography variant="Base" className="mb-2 text-gray-900">
            Card Number
          </Typography>
          <div className="border rounded-md p-3">
            <CardNumberElement options={ELEMENT_OPTIONS} />
          </div>
        </div>

        {/* Expiry + CVC side by side */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Typography variant="Base" className="mb-2 text-gray-900">
              Expiry Date
            </Typography>
            <div className="border rounded-md p-3">
              <CardExpiryElement options={ELEMENT_OPTIONS} />
            </div>
          </div>
          <div>
            <Typography variant="Base" className="mb-2 text-gray-900">
              CVC
            </Typography>
            <div className="border rounded-md p-3">
              <CardCvcElement options={ELEMENT_OPTIONS} />
            </div>
          </div>
        </div>

        {error && (
          <Typography variant="SM" className="text-red-500 mt-2">
            {error}
          </Typography>
        )}

        {/* Actions */}
        <div className="flex justify-end space-x-3 mt-8 pt-4 border-t border-gray-200">
          <Button
            type={isLoading ? "Disabled" : "Default"}
            buttonText="Cancel"
            onClick={handleCancel}
            className="px-6"
          />
          <Button
            type={isLoading ? "Disabled" : "Primary"}
            buttonText={isLoading ? "Saving..." : "Save"}
            onClick={handleSave}
            className="px-6"
            loading={isLoading}
          />
        </div>
      </div>
    </Modal>
  );
};

export default AddPaymentMethodModal;
